import { Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { tokens } from 'styling/tokens'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { CheckIcon } from '../icons/CheckIcon'
import { ExclamationMarkIcon } from '../icons/ExclamationMarkIcon'
import { ClockIcon } from '../icons/ClockIcon'
import { useEffect, useState } from 'react'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { checkOut, getAccommodationGuests } from 'features/accommodation/accommodationSlice'
import { IGetAllAccommodationGuestsSingleResponse } from 'types/api/accommodationApi.interface'
import { formatDateForDisplay, getDateDifference } from 'utils/helpers'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { ExtendStayModal } from './ExtendStayModal'
import { NoData } from 'components/NoData'
import { TotalIcon } from '../icons/TotalIcon'
import {
  StatBand,
  StatCardWrap,
  GuestCardsContainer,
  GuestCard,
  GuestHead,
  GuestName,
  GuestMetaRow,
  GuestId,
  InfoTiles,
  InfoTile,
  Label,
  Value,
  InfoBlock,
  Breadcrumb,
  BreadcrumbBed,
  DurationRow,
  DurationLabel,
  DurationValue,
  GuestActions,
} from './Guests.styled'

const colors = {
  red: tokens.status.danger.fg,
  yellow: tokens.status.warning.fg,
  green: tokens.status.success.fg,
  blue: tokens.status.info.fg,
}

const cardAccent = (title: string) => {
  switch (title) {
    case 'Total':
      return colors.blue
    case 'Overstayed':
      return colors.red
    case 'Expiring Soon':
      return colors.yellow
    default:
      return colors.green
  }
}

const cardIcon = (title: string) => {
  switch (title) {
    case 'Total':
      return <TotalIcon />
    case 'Overstayed':
      return <ExclamationMarkIcon />
    case 'Expiring Soon':
      return <ClockIcon color={colors.yellow} />
    default:
      return <CheckIcon />
  }
}

export const Guests = () => {
  const [active, setActive] = useState('All Guests')
  const [params, setParams] = useState<any>({ page: 1, limit: 99, personType: 2, filter: 1 })
  const dispatch = useDispatch<AppDispatch>()
  const { guests } = useSelector((state: RootState) => state.accomodation)
  const [modal, setModal] = useState({ open: false, id: null })

  const handleActiveChange = (status: string, filter: number) => {
    setActive(status)
    setParams({ ...params, filter })
  }

  useEffect(() => {
    dispatch(getAccommodationGuests(params))
  }, [dispatch, params])

  const StatusBox = ({ title, count, active, onClick }) => (
    <StatCardWrap $active={active} $color={cardAccent(title)} onClick={onClick}>
      <MetricCard
        layout='inline'
        label={title || 'Total'}
        value={count || 0}
        icon={cardIcon(title)}
        accentColor={cardAccent(title)}
      />
    </StatCardWrap>
  )

  const handleCheckOut = (id: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(checkOut(id))
          await dispatch(getAccommodationGuests(params))
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
          f: setParams,
        },
        word: 'check out this person',
      }),
    )
  }

  const Guest = ({ data }: { data: IGetAllAccommodationGuestsSingleResponse }) => {
    const guest = data.accommodationGuest

    return (
      <GuestCard>
        <GuestHead>
          <GuestName>
            {guest?.firstName} {guest?.lastName}
          </GuestName>
          <GuestMetaRow>
            <StatusPill status='neutral'>Guest</StatusPill>
            <GuestId>ID: {guest?.identificationNumber || '/'}</GuestId>
          </GuestMetaRow>
        </GuestHead>

        <InfoTiles>
          <InfoTile>
            <Label>Check-In</Label>
            <Value>{formatDateForDisplay(data.checkInTimestamp)}</Value>
          </InfoTile>
          <InfoTile>
            <Label>Check-Out</Label>
            <Value>{formatDateForDisplay(data.expectedCheckOutTimestamp)}</Value>
          </InfoTile>
        </InfoTiles>

        <InfoBlock>
          <Label>Bed Location</Label>
          <Breadcrumb>
            <LocationIcon />
            {data.campName || '/'}
            <NextIcon />
            {data.blockName || '/'}
            <NextIcon />
            {data.roomName || '/'}
            <NextIcon />
            <BreadcrumbBed>{data.bedName || '/'}</BreadcrumbBed>
          </Breadcrumb>
        </InfoBlock>

        <Flex gap='2rem'>
          <InfoBlock>
            <Label>Gender</Label>
            <Value>{(guest?.gender || '/').toUpperCase()}</Value>
          </InfoBlock>
          <InfoBlock>
            <Label>Requested By</Label>
            <Value>
              {guest?.requestedBy ? `${guest.requestedBy.firstName} ${guest.requestedBy.lastName}` : '/'}
            </Value>
          </InfoBlock>
        </Flex>

        <DurationRow>
          <DurationLabel>Total Stay Duration</DurationLabel>
          <DurationValue>{getDateDifference(data.checkInTimestamp, data.expectedCheckOutTimestamp)}</DurationValue>
        </DurationRow>

        {!data.actualCheckOutTimestamp && (
          <GuestActions>
            <Button variant='secondary' onClick={() => setModal({ open: true, id: data.id })}>
              Extend Stay
            </Button>
            <Button variant='success' onClick={() => handleCheckOut(data.id)}>
              Check Out
            </Button>
          </GuestActions>
        )}
      </GuestCard>
    )
  }

  return (
    <>
      <StatBand>
        <StatusBox title='Total' count={guests?.total || 0} active={active === 'All Guests'} onClick={() => handleActiveChange('All Guests', 1)} />
        <StatusBox title='Overstayed' count={guests?.overstayed || 0} active={active === 'Overstayed'} onClick={() => handleActiveChange('Overstayed', 2)} />
        <StatusBox title='Expiring Soon' count={guests?.expiringSoon || 0} active={active === 'Expiring Soon'} onClick={() => handleActiveChange('Expiring Soon', 3)} />
        <StatusBox title='Active' count={guests?.active || 0} active={active === 'Active'} onClick={() => handleActiveChange('Active', 4)} />
      </StatBand>

      <GuestCardsContainer>
        {guests?.data.length ? guests.data.map((guest, i) => guest && <Guest key={i} data={guest} />) : null}
      </GuestCardsContainer>
      {!guests?.data.length && (
        <Flex width='100%' justcenter height='10rem'>
          <NoData text='No guests found for selected criteria' />
        </Flex>
      )}
      {modal.open && <ExtendStayModal closeModal={() => setModal({ open: false, id: null })} id={modal.id} />}
    </>
  )
}
